/**
 * The user's own arrangement of the sidebar, and how it survives a reload.
 *
 * Stored as ORDERS OF IDS, never as a copy of the tree. That distinction is the
 * whole design: the tree itself is rebuilt from permissions on every load, so a
 * saved arrangement that held its own copy of the menu would keep showing
 * entries a user has lost access to, and would never show entries they gain.
 * Holding only the order means the arrangement and the permissions can each
 * change without the other going stale.
 *
 * It also fixes the failure that the saved-column-visibility code in
 * `saas-data-table` still has: there, anything absent from the stored list is
 * hidden forever, so a column added later never appears for an existing user.
 * Here an id the saved order has never seen is APPENDED in its default position
 * rather than dropped, so a new menu entry always shows up.
 *
 * There is no user-preferences endpoint on the API, so this uses localStorage,
 * keyed per user — the same pattern as `orgStructure.rail` and `saas-cols:`.
 * When such an endpoint exists, only `readNavOrder`/`writeNavOrder` change.
 *
 * This file is storage and pure transforms ONLY. The live arrangement lives in
 * the Redux store (`features/navigation/navSlice`), because two screens read it
 * — the sidebar and the customize page — and a `useState` here gave each of
 * them a private copy: saving on one left the other showing the old order until
 * the app was reloaded.
 */
import type { NavGroup } from './nav-model';

export interface NavOrder {
  /** Group ids, in the order they should read. */
  groups: string[];
  /** Group id -> its collection ids, in order. */
  collections: Record<string, string[]>;
  /** Collection id -> its item ids, in order. */
  items: Record<string, string[]>;
}

export const EMPTY_ORDER: NavOrder = { groups: [], collections: {}, items: {} };

const KEY_PREFIX = 'nav.order.v1';

export const navStorageKey = (userId: string) => `${KEY_PREFIX}:${userId || 'anon'}`;

export const readNavOrder = (userId: string): NavOrder => {
  try {
    const raw = localStorage.getItem(navStorageKey(userId));
    if (!raw) return EMPTY_ORDER;
    const parsed = JSON.parse(raw);
    // Trust nothing that came out of storage: a half-written or hand-edited
    // value must not be able to blank the menu.
    return {
      groups: Array.isArray(parsed?.groups) ? parsed.groups.filter((x: unknown) => typeof x === 'string') : [],
      collections: parsed?.collections && typeof parsed.collections === 'object' ? parsed.collections : {},
      items: parsed?.items && typeof parsed.items === 'object' ? parsed.items : {},
    };
  } catch {
    return EMPTY_ORDER;
  }
};

export const writeNavOrder = (userId: string, order: NavOrder) => {
  try {
    localStorage.setItem(navStorageKey(userId), JSON.stringify(order));
  } catch {
    /* Storage refused (private window, quota). The arrangement still applies
       for this session; it just will not outlive it. Not worth failing Save. */
  }
};

/**
 * Sort `list` by `saved`, keeping anything `saved` has not heard of.
 *
 * Known ids come first in the saved order; unknown ids keep their default
 * position relative to each other and follow. That is what makes a newly
 * granted menu entry appear instead of silently vanishing.
 */
const applyOrder = <T extends { id: string }>(list: T[], saved?: string[]): T[] => {
  if (!saved || saved.length === 0) return list;
  const rank = new Map(saved.map((id, i) => [id, i]));
  const known = list.filter((x) => rank.has(x.id)).sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
  const fresh = list.filter((x) => !rank.has(x.id));
  return [...known, ...fresh];
};

/** Rearrange a permission-filtered tree by a saved order. Pure. */
export const orderNav = (nav: NavGroup[], order: NavOrder): NavGroup[] =>
  applyOrder(nav, order.groups).map((g) => ({
    ...g,
    collections: applyOrder(g.collections, order.collections[g.id]).map((c) => ({
      ...c,
      children: applyOrder(c.children, order.items[c.id]),
    })),
  }));

/** Read the arrangement back off a tree, to save what the user just built. */
export const orderFromNav = (nav: NavGroup[]): NavOrder => ({
  groups: nav.map((g) => g.id),
  collections: Object.fromEntries(nav.map((g) => [g.id, g.collections.map((c) => c.id)])),
  items: Object.fromEntries(
    nav.flatMap((g) => g.collections.map((c) => [c.id, c.children.map((i) => i.id)] as const))
  ),
});
